import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { grey } from "@/theme/palette";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  accessTokenState,
  companyNameState,
  companyState,
} from "@/client/state";
import { editingFileState } from "@/client/state";
import { useContext, useState } from "react";
import { settingsState } from "@/state/settings";
import { GenAiContext } from "@/client/context";

interface Props {
  fileName: string;
}

const FileMessage = ({ fileName }: Props) => {
  const companyName = useRecoilValue(companyNameState);
  const setEditingFile = useSetRecoilState(editingFileState);
  const [_, setSettings] = useRecoilState(settingsState);
  const [isLoading, setIsLoading] = useState(false);
  const [companyId] = useRecoilState(companyState);

  const accessToken = useRecoilValue(accessTokenState);
  const apiClient = useContext(GenAiContext);

  // Set editing file on load
  // useEffect(() => {
  //   if (fileName) {
  //     setEditingFile({
  //       // file: "http://43.218.122.237:9000/qnb/PRTL_analyze_report.docx",
  //       file: "/example.pdf",
  //       name: fileName,
  //     });
  //   }
  // }, [fileName, setEditingFile]);

  const handlePreview = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const blob = await apiClient.getDocument(companyId, accessToken || "");

      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);

        setEditingFile({
          file: url, // previewable blob URL
          name: fileName,
        });

        // open editor panel
        setSettings((prev) => ({
          ...prev,
          isTextEditorOpen: true,
          isChatHistoryOpen: false,
        }));
      } else {
        console.error("API did not return Blob:", blob);
      }
    } catch (err) {
      console.error("Failed to get document.", err);
    } finally {
      setIsLoading(false);
    }
  };

  async function downloadReport() {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const blob = await apiClient.getDocument(companyId, accessToken || "");
      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileName}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("API error:", blob);
      }
    } catch (err) {
      console.error("Failed to get document.", err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1.5,
        borderRadius: "0.7rem",
        backgroundColor: grey[200],
        border: `1px solid ${grey[400]}`,
      }}
    >
      {/* Left side: icon + text */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <InsertDriveFileOutlinedIcon fontSize="large" color="action" />
        <Stack>
          <Typography variant="body1" fontWeight={600} noWrap>
            {fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {companyName}
          </Typography>
        </Stack>
      </Stack>

      {/* Right side: actions */}
      <Stack direction="row" spacing={1}>
        <Tooltip title="Download file">
          <span>
            <IconButton
              size="small"
              onClick={downloadReport}
              disabled={isLoading}
            >
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Preview file">
          <span>
            <IconButton
              size="small"
              onClick={handlePreview}
              disabled={isLoading}
            >
              <LaunchOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export { FileMessage };
